import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { User, Menu } from "lucide-react";
import NotificationDropdown from "./NotificationDropdown";
import UserProfileDialog from "./UserProfileDialog";

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  return (
    <header className="bg-white/95 backdrop-blur-xl border-b border-slate-200/60 px-3 sm:px-4 lg:px-6 py-3 lg:py-4 shadow-lg sticky top-0 z-50">
      <div className="flex justify-between items-center max-w-7xl mx-auto">
        {/* Left side - Menu button (mobile) + Logo */}
        <div className="flex items-center space-x-3">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 hover:from-blue-100 hover:to-indigo-100 border border-slate-200/60 transition-all duration-300 hover:shadow-md"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>

          {/* Logo - Conditional rendering based on screen size */}
          <div className="relative">
            {/* Small screen logo */}
            <div className="md:hidden w-10 h-10 rounded-xl overflow-hidden ring-2 ring-blue-100 shadow-md">
              <img
                src="/logo-s.png"
                alt="ATIAM College Logo"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Medium and large screen logo */}
            <div className="hidden md:block">
              <img
                src="/school-logo.png"
                alt="ATIAM College Logo"
                className="lg:w-60 lg:h-auto md:w-48 md:h-auto drop-shadow-sm"
              />
            </div>
          </div>
        </div>

        {/* Right side elements */}
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* Notification Dropdown */}
          <NotificationDropdown
            isOpen={showNotifications}
            onToggle={() => setShowNotifications(!showNotifications)}
          />

          {/* User Info and Avatar */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-bold text-slate-900">
                {user?.email?.split("@")[0] || "Guest User"}
              </div>
              <div className="text-xs font-medium capitalize bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
                {user?.roles?.[0] || "Student"}
              </div>
            </div>
            <div
              onClick={() => setShowProfileDialog(true)}
              className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden ring-2 ring-blue-100 ${
                !user?.profileDetails?.profilePicture || profileImageError
                  ? "bg-gradient-to-br from-blue-500 to-indigo-500"
                  : ""
              }`}
            >
              {user?.profileDetails?.profilePicture && !profileImageError ? (
                <img
                  src={user.profileDetails.profilePicture}
                  alt={`${user.firstName || "User"} ${
                    user.lastName || ""
                  }`.trim()}
                  className="w-full h-full object-cover"
                  onError={() => setProfileImageError(true)}
                />
              ) : (
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Dialog */}
      <UserProfileDialog
        isOpen={showProfileDialog}
        onClose={() => setShowProfileDialog(false)}
      />
    </header>
  );
};

export default Header;
